import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Trees, Lightbulb, Square, Columns, Leaf, LayoutGrid } from "lucide-react";

const FEATURES = [
  {
    key: "patio",
    label: "Patio",
    icon: LayoutGrid,
    image: "https://images.unsplash.com/photo-1775733923991-e7223f9f44bc?crop=entropy&cs=srgb&fm=jpg&q=90&w=1600",
  },
  {
    key: "deck",
    label: "Deck",
    icon: Square,
    image: "https://images.unsplash.com/photo-1764351969978-1d21525cb76f?crop=entropy&cs=srgb&fm=jpg&q=90&w=1600",
  },
  {
    key: "firepit",
    label: "Fire Pit",
    icon: Flame,
    image: "https://images.unsplash.com/photo-1769628241887-8c6b9e1e09f8?crop=entropy&cs=srgb&fm=jpg&q=90&w=1600",
  },
  {
    key: "pergola",
    label: "Pergola",
    icon: Columns,
    image: "https://images.unsplash.com/photo-1761637823293-f96835ec5038?crop=entropy&cs=srgb&fm=jpg&q=90&w=1600",
  },
  {
    key: "trees",
    label: "Trees",
    icon: Trees,
    image: "https://images.unsplash.com/photo-1761637823407-ef47925c2714?crop=entropy&cs=srgb&fm=jpg&q=90&w=1600",
  },
  {
    key: "lighting",
    label: "Lighting",
    icon: Lightbulb,
    image: "https://images.unsplash.com/photo-1764351969978-1d21525cb76f?crop=entropy&cs=srgb&fm=jpg&q=90&w=1600",
  },
  {
    key: "landscaping",
    label: "Landscaping",
    icon: Leaf,
    image: "https://images.unsplash.com/photo-1761637823293-f96835ec5038?crop=entropy&cs=srgb&fm=jpg&q=90&w=1600",
  },
];

export default function BackyardExplorer() {
  const [active, setActive] = useState("firepit");
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 2;
    const y = ((e.clientY - r.top) / r.height - 0.5) * 2;
    setTilt({ x, y });
  };

  const activeFeature = FEATURES.find((f) => f.key === active);

  return (
    <div data-testid="backyard-explorer" className="relative">
      <div
        className="relative h-[520px] md:h-[640px] bg-[#0a0a0a] border border-[#D8D6D0]/10 overflow-hidden"
        onMouseMove={onMove}
        onMouseLeave={() => setTilt({ x: 0, y: 0 })}
        style={{ perspective: "1400px" }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0"
            style={{
              transform: `rotateY(${tilt.x * 3}deg) rotateX(${-tilt.y * 2}deg) scale(1.03)`,
              transformStyle: "preserve-3d",
              transition: "transform 0.4s ease-out",
            }}
          >
            <img
              src={activeFeature.image}
              alt={activeFeature.label}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#151515] via-[#151515]/30 to-[#151515]/40" />
          </motion.div>
        </AnimatePresence>

        {/* Animated grid overlay for futuristic feel */}
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{
          backgroundImage: "linear-gradient(to right, #F5F3EF 1px, transparent 1px), linear-gradient(to bottom, #F5F3EF 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }} />

        <div className="absolute top-4 left-4 overline text-[#D8D6D0]/60">
          Interactive Composer · Hover to tilt
        </div>

        <motion.div
          key={active + "-label"}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="absolute bottom-6 left-6 md:bottom-10 md:left-10"
        >
          <div className="overline text-[#8C1D30] mb-2">{activeFeature.label}</div>
          <div className="font-serif-display text-3xl md:text-5xl">Composition · {activeFeature.label}</div>
        </motion.div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {FEATURES.map((f) => {
          const Icon = f.icon;
          const on = active === f.key;
          return (
            <button
              key={f.key}
              data-testid={`toggle-${f.key}`}
              onClick={() => setActive(f.key)}
              className={`flex items-center gap-2 px-4 py-3 text-xs tracking-widest uppercase transition-all duration-300 border ${
                on
                  ? "bg-[#F5F3EF] text-[#151515] border-[#F5F3EF]"
                  : "bg-transparent text-[#D8D6D0]/70 border-[#D8D6D0]/20 hover:border-[#D8D6D0]/60"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {f.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
